import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Share2, Copy, Check, ExternalLink, X, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function ShareDialog({ open, onOpenChange, resumeId }) {
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [share, setShare] = useState(null); // { active, token, expires_at }
  const [copied, setCopied] = useState(false);

  const fetchShare = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/resumes/${resumeId}/share`);
      setShare(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && resumeId) fetchShare();
    if (!open) setCopied(false);
     
  }, [open, resumeId]);

  const generate = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/resumes/${resumeId}/share`, {});
      setShare(data);
      toast.success("Share link created — expires in 7 days");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this link? Anyone with the link will lose access immediately.")) return;
    setWorking(true);
    try {
      await api.delete(`/resumes/${resumeId}/share`);
      setShare({ active: false });
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(false);
    }
  };

  const fullUrl = share?.token ? `${window.location.origin}/r/${share.token}` : "";

  const copy = async () => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — please copy it manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Share2 className="w-4 h-4 text-blue-600" /> Share a read-only link
          </DialogTitle>
          <DialogDescription>
            Anyone with the link can view your resume — they can&apos;t edit, download editable copies, or see your private references.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : share?.active ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-mono-display mb-1.5">Share link</div>
              <div className="flex items-center gap-2">
                <Input value={fullUrl} readOnly className="font-mono text-xs bg-white" data-testid="share-link-input" />
                <Button type="button" size="sm" variant="outline" onClick={copy} data-testid="share-copy-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
                <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="button" size="sm" variant="outline" data-testid="share-open-btn">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="text-slate-500 flex items-center gap-1.5">
                <Lock className="w-3 h-3" /> Expires {fmtDate(share.expires_at)}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={revoke}
                disabled={working}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                data-testid="share-revoke-btn"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Revoke link
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 leading-relaxed bg-blue-50/50 border border-blue-100 rounded-md p-3">
              <div className="font-semibold text-slate-700">What viewers see</div>
              Your resume as a read-only page. They can print or save a PDF from their browser. References are hidden.
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Generate a private link to share your resume with recruiters. It expires automatically in <span className="font-semibold">7 days</span>, and you can revoke it any time.
            </p>
            <Button
              type="button"
              onClick={generate}
              disabled={working}
              className="bg-slate-900 hover:bg-slate-800 w-full"
              data-testid="share-generate-btn"
            >
              {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-2" /> Create share link</>}
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
